import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../../components/Loading';
import InputCpfCnpj from '../../../components/MaskedInput/InputCpfCnpj';
import {
   getCustomer,
   createCustomer,
   updateCustomer,
} from '../../../services/customers/customersApi';
import { sanitizeDoc, validateCpfCnpj } from '../../../utils/validateCpfCnpj';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../styles/layout/ActionBar';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../styles/forms/Field';

const FIELD_ERROR_STYLE = {
   fontSize: 11,
   color: '#b71c1c',
   marginTop: 2,
};

// Deriva o person_type (PF/PJ) a partir do doc, espelhando o backend:
// CPF → PF, CNPJ → PJ. Sem doc (ou tamanho inválido) → null.
function derivePersonType(docDigits) {
   const { kind } = validateCpfCnpj(docDigits);
   if (kind === 'CPF') return 'PF';
   if (kind === 'CNPJ') return 'PJ';
   return null;
}

export default function ClienteForm() {
   const { id } = useParams();
   const navigate = useNavigate();
   const isEditMode = Boolean(id);

   // ─── Campos ───────────────────────────────────────────────────────────────
   const [name, setName] = useState('');
   const [docInput, setDocInput] = useState(''); // valor com máscara
   const [personType, setPersonType] = useState(''); // só usado quando NÃO há doc
   const [email, setEmail] = useState('');
   const [phone, setPhone] = useState('');
   const [refiltekId, setRefiltekId] = useState('');
   const [status, setStatus] = useState('active');

   const [fieldErrors, setFieldErrors] = useState({});
   const [isLoading, setIsLoading] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);

   const docDigits = sanitizeDoc(docInput);
   const derivedPersonType = derivePersonType(docDigits);
   const hasDoc = docDigits.length > 0;

   // ─── Carrega no modo edição ─────────────────────────────────────────────
   const fetchCustomerForEdit = useCallback(async () => {
      setIsLoading(true);
      try {
         const customer = await getCustomer(id);
         if (!customer) {
            toast.error('Cliente não encontrado.');
            navigate('/comercial/clientes');
            return;
         }
         setName(customer.name ?? '');
         setDocInput(customer.doc ?? '');
         setPersonType(customer.person_type ?? '');
         setEmail(customer.email ?? '');
         setPhone(customer.phone ?? '');
         setRefiltekId(customer.refiltek_id ?? '');
         setStatus(customer.status ?? 'active');
      } catch (err) {
         const apiMessage =
            err?.response?.data?.errors?.[0]?.message ??
            err?.response?.data?.message ??
            'Erro ao carregar cliente.';
         toast.error(apiMessage);
         navigate('/comercial/clientes');
      } finally {
         setIsLoading(false);
      }
   }, [id, navigate]);

   useEffect(() => {
      if (isEditMode) fetchCustomerForEdit();
   }, [isEditMode, fetchCustomerForEdit]);

   // Limpa o erro de um campo quando o usuário o edita.
   const clearFieldError = (field) => {
      setFieldErrors((prev) => {
         if (!prev[field]) return prev;
         const next = { ...prev };
         delete next[field];
         return next;
      });
   };

   // ─── Submit ───────────────────────────────────────────────────────────────
   async function handleSubmit(event) {
      event.preventDefault();
      if (isSubmitting) return;

      // Validação client-side mínima.
      const clientErrors = {};
      if (!name.trim()) clientErrors.name = 'Nome é obrigatório.';
      if (hasDoc) {
         const check = validateCpfCnpj(docDigits);
         if (!check.valid) clientErrors.doc = check.reason;
      }
      if (Object.keys(clientErrors).length > 0) {
         setFieldErrors(clientErrors);
         toast.error('Verifique os campos destacados.');
         return;
      }

      // Monta o payload. doc só com dígitos; no PUT, doc vazio = null (limpar).
      const payload = {
         name: name.trim(),
         email: email.trim() || null,
         phone: phone.trim() || null,
         refiltek_id: refiltekId.trim() || null,
         status,
      };

      if (hasDoc) {
         payload.doc = docDigits;
         // person_type é derivado pelo backend quando há doc — não enviamos.
      } else {
         payload.doc = isEditMode ? null : undefined;
         if (personType) payload.person_type = personType;
      }

      setIsSubmitting(true);
      setFieldErrors({});

      try {
         if (isEditMode) {
            const { customer, message } = await updateCustomer(id, payload);
            toast.success(message);
            navigate(`/comercial/clientes/${customer?.id ?? id}`);
         } else {
            const { customer, message } = await createCustomer(payload);
            toast.success(message);
            navigate(`/comercial/clientes/${customer?.id}`);
         }
      } catch (err) {
         handleSubmitError(err);
      } finally {
         setIsSubmitting(false);
      }
   }

   // 409 (doc/refiltek_id duplicado) vem com field interno (active_doc_slot /
   // active_refiltek_slot), então NÃO vinculamos ao campo — tratamos por code.
   // 422 de validação vem com field real, mapeado em fieldErrors.
   function handleSubmitError(err) {
      const responseErrors = err?.response?.data?.errors;
      const httpStatus = err?.response?.status;

      const isUniqueConflict =
         httpStatus === 409 ||
         (Array.isArray(responseErrors) &&
            responseErrors.some((e) => e?.code === 'unique'));

      if (isUniqueConflict) {
         toast.error('CPF/CNPJ ou código Refiltek já cadastrado.');
         return;
      }

      if (Array.isArray(responseErrors) && responseErrors.length > 0) {
         const byField = {};
         responseErrors.forEach((entry) => {
            if (entry?.field) byField[entry.field] = entry.message;
         });
         if (Object.keys(byField).length > 0) {
            setFieldErrors(byField);
            toast.error(
               err?.response?.data?.message ?? 'Verifique os campos destacados.',
            );
            return;
         }
      }

      const apiMessage =
         err?.response?.data?.message ??
         err?.message ??
         'Erro ao salvar cliente.';
      toast.error(apiMessage);
   }

   const backTo = isEditMode ? `/comercial/clientes/${id}` : '/comercial/clientes';

   return (
      <Container>
         <Loading isLoading={isLoading || isSubmitting} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>
                  {isEditMode ? 'Editar cliente' : 'Novo cliente'}
               </PageTitle>
               <PageSubtitle>
                  {isEditMode
                     ? 'Atualize os dados do cliente.'
                     : 'Preencha os dados do cliente. Endereços são adicionados na tela de detalhe.'}
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               <button
                  type="button"
                  onClick={() => navigate(backTo)}
                  disabled={isSubmitting}
               >
                  Cancelar
               </button>
            </PageHeaderActions>
         </PageHeader>

         <form onSubmit={handleSubmit}>
            <Section>
               <SectionHeader>
                  <SectionTitle>Dados do cliente</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid>
                     <Field className="col-8">
                        <label htmlFor="customer-name">
                           Nome
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <input
                           id="customer-name"
                           type="text"
                           maxLength={150}
                           value={name}
                           onChange={(e) => {
                              setName(e.target.value);
                              clearFieldError('name');
                           }}
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.name || undefined}
                        />
                        {fieldErrors.name && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.name}
                           </span>
                        )}
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="customer-status">Status</label>
                        <select
                           id="customer-status"
                           value={status}
                           onChange={(e) => setStatus(e.target.value)}
                           disabled={isSubmitting}
                        >
                           <option value="active">Ativo</option>
                           <option value="inactive">Inativo</option>
                        </select>
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="customer_doc">CPF/CNPJ</label>
                        <InputCpfCnpj
                           name="customer_doc"
                           value={docInput}
                           onChange={(e) => {
                              setDocInput(e.target.value);
                              clearFieldError('doc');
                           }}
                           disabled={isSubmitting}
                        />
                        {fieldErrors.doc && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.doc}
                           </span>
                        )}
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="customer-person-type">
                           Tipo de pessoa
                        </label>
                        {hasDoc ? (
                           // Com doc, o tipo é derivado pelo backend — read-only.
                           <input
                              id="customer-person-type"
                              type="text"
                              value={derivedPersonType ?? '—'}
                              readOnly
                              disabled
                           />
                        ) : (
                           <select
                              id="customer-person-type"
                              value={personType}
                              onChange={(e) => setPersonType(e.target.value)}
                              disabled={isSubmitting}
                           >
                              <option value="">Não informado</option>
                              <option value="PF">PF — Pessoa Física</option>
                              <option value="PJ">PJ — Pessoa Jurídica</option>
                           </select>
                        )}
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="customer-refiltek-id">
                           Código Refiltek
                        </label>
                        <input
                           id="customer-refiltek-id"
                           type="text"
                           maxLength={20}
                           value={refiltekId}
                           onChange={(e) => {
                              setRefiltekId(e.target.value);
                              clearFieldError('refiltek_id');
                           }}
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.refiltek_id || undefined}
                        />
                        {fieldErrors.refiltek_id && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.refiltek_id}
                           </span>
                        )}
                     </Field>

                     <Field className="col-6">
                        <label htmlFor="customer-email">E-mail</label>
                        <input
                           id="customer-email"
                           type="email"
                           maxLength={150}
                           value={email}
                           onChange={(e) => {
                              setEmail(e.target.value);
                              clearFieldError('email');
                           }}
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.email || undefined}
                        />
                        {fieldErrors.email && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.email}
                           </span>
                        )}
                     </Field>

                     <Field className="col-6">
                        <label htmlFor="customer-phone">Telefone</label>
                        <input
                           id="customer-phone"
                           type="tel"
                           maxLength={30}
                           value={phone}
                           onChange={(e) => {
                              setPhone(e.target.value);
                              clearFieldError('phone');
                           }}
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.phone || undefined}
                        />
                        {fieldErrors.phone && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.phone}
                           </span>
                        )}
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>

            <ActionBar>
               <ActionBarLeft />
               <ActionBarRight>
                  <button
                     type="button"
                     onClick={() => navigate(backTo)}
                     disabled={isSubmitting}
                  >
                     Cancelar
                  </button>
                  <button type="submit" disabled={isSubmitting}>
                     {isSubmitting
                        ? 'Salvando...'
                        : isEditMode
                          ? 'Salvar alterações'
                          : 'Cadastrar cliente'}
                  </button>
               </ActionBarRight>
            </ActionBar>
         </form>
      </Container>
   );
}
